// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { ListPageShell } from '@/components/shared/ListPageShell';
import { DataTableRow } from '@/components/shared/DataTableRow';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useExcelExport } from '@/hooks/useExcelExport';
import { downloadExcelTemplate, type ExcelColumnDef } from '@/lib/excel.utils';
import { ExportImportButtons } from '@/components/shared/ExportImportButtons';
import { InlineFilterRow } from '@/components/shared/InlineFilterRow';
import type { FilterField } from '@/components/shared/FilterBar';
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: 'code', label: 'Mã Kho', width: 16 },
  { key: 'name', label: 'Tên Kho', width: 25 },
  { key: 'type', label: 'Phân Loại', width: 18 },
  { key: 'managerName', label: 'Người Quản Lý', width: 20 },
  {
    key: 'status',
    label: 'Trạng Thái',
    width: 16,
    formatter: (_v, row: any) => (row.status === 'ACTIVE' ? 'Hoạt động' : 'Tạm khóa'),
  },
];

const FILTER_FIELDS: FilterField[] = [
  { id: 'code', label: 'Mã kho', type: 'text' },
  { id: 'name', label: 'Tên kho', type: 'text' },
  {
    id: 'type',
    label: 'Phân loại',
    type: 'select',
    options: [
      { value: 'MAIN', label: 'Kho tổng' },
      { value: 'MATERIAL', label: 'Kho nguyên vật liệu' },
      { value: 'FG', label: 'Kho thành phẩm' },
      { value: 'WIP', label: 'Kho bán thành phẩm' },
      { value: 'TOOLING', label: 'Kho CCDC' },
      { value: 'TECH', label: 'Kho vật tư kỹ thuật' },
      { value: 'SCRAP', label: 'Kho phế liệu' },
    ],
  },
  { id: 'managerName', label: 'Người quản lý', type: 'text' },
  {
    id: 'status',
    label: 'Trạng thái',
    type: 'select',
    options: [
      { value: 'ACTIVE', label: 'Hoạt động' },
      { value: 'INACTIVE', label: 'Tạm khóa' },
    ],
  },
];

export function WarehouseList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(50);
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['warehouses', 'list', { limit: 200, page: 1 }],
    queryFn: () => trpcClient.warehouses.list.query({ limit: 200, page: 1 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => trpcClient.warehouses.delete.mutate({ id }),
    onSuccess: () => {
      toast.success('Xóa kho thành công');
      queryClient.invalidateQueries({ queryKey: ['warehouses', 'list'] });
    },
    onError: (error: any) => {
      toast.error(`Lỗi xóa kho: ${error.message}`);
    },
  });

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Bạn có chắc chắn muốn xóa kho "${name}"? Thao tác này không thể hoàn tác.`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleClearAll = () => {
    setFilterValues({});
    setPage(1);
  };

  const allItems = data?.items || [];

  const filteredData = useMemo(() => {
    return allItems.filter((item: any) => {
      if (filterValues.code && !item.code.toLowerCase().includes(filterValues.code.toLowerCase())) {
        return false;
      }
      if (filterValues.name && !item.name.toLowerCase().includes(filterValues.name.toLowerCase())) {
        return false;
      }
      if (filterValues.type && filterValues.type !== 'ALL' && item.type !== filterValues.type) {
        return false;
      }
      if (filterValues.managerName && !item.managerName?.toLowerCase().includes(filterValues.managerName.toLowerCase())) {
        return false;
      }
      if (filterValues.status && filterValues.status !== 'ALL' && item.status !== filterValues.status) {
        return false;
      }
      return true;
    });
  }, [allItems, filterValues]);

  const pagedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const { handleExport, isExporting } = useExcelExport({
    filename: 'danh_sach_kho',
    columns: EXPORT_COLUMNS,
    localData: filteredData,
  });

  return (
    <ListPageShell
      unitLabel="kho"
      filterFields={FILTER_FIELDS}
      filterValues={filterValues}
      showFilter={showFilter}
      onToggleFilter={() => setShowFilter((prev) => !prev)}
      onClearAll={handleClearAll}
      currentPage={page}
      pageSize={pageSize}
      totalItems={filteredData.length}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      actions={
        <ExportImportButtons
          onExport={() => handleExport()}
          isExporting={isExporting}
          onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, 'mau_danh_sach_kho')}
        >
          <Button
            size="sm"
            className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
            onClick={() => navigate({ to: '/warehouse/master-data/new' } as any)}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Thêm kho mới
          </Button>
        </ExportImportButtons>
      }
    >
      <Table className="min-w-[1000px]">
        <TableHeader className="apv-table-header">
          <TableRow>
            <TableHead className="apv-col-code w-[140px] font-semibold">Mã Kho</TableHead>
            <TableHead className="font-semibold min-w-[220px]">Tên Kho</TableHead>
            <TableHead className="font-semibold w-[160px]">Loại</TableHead>
            <TableHead className="font-semibold min-w-[180px]">Người quản lý</TableHead>
            <TableHead className="font-semibold w-[140px]">Trạng thái</TableHead>
            <TableHead className="text-right apv-col-actions w-[100px]">Thao tác</TableHead>
          </TableRow>
          {showFilter && (
            <InlineFilterRow
              columns={[
                { key: 'code' },
                { key: 'name' },
                { key: 'type' },
                { key: 'managerName' },
                { key: 'status' },
                null,
              ]}
              fields={FILTER_FIELDS}
              values={filterValues}
              onChange={handleFilterChange}
            />
          )}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center text-slate-400">
                Đang tải dữ liệu...
              </TableCell>
            </TableRow>
          ) : pagedItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center text-slate-500">
                Không tìm thấy kho nào phù hợp.
              </TableCell>
            </TableRow>
          ) : (
            pagedItems.map((item: any) => (
              <DataTableRow
                key={item.id}
                id={item.id}
                className="group cursor-pointer hover:bg-slate-50"
                onClick={() => navigate({ to: `/warehouse/master-data/${item.id}` } as any)}
              >
                <TableCell className="font-medium text-blue-600 apv-col-code">{item.code}</TableCell>
                <TableCell className="font-semibold">{item.name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="bg-slate-100">{item.type}</Badge>
                </TableCell>
                <TableCell>{item.managerName || '—'}</TableCell>
                <TableCell>
                  {item.status === 'ACTIVE' ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-200">
                      Hoạt động
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
                      Tạm khóa
                    </Badge>
                  )}
                  {item.allowNegativeStock !== false && (
                    <Badge
                      variant="outline"
                      className="ml-1 bg-amber-50 text-amber-700 border-amber-200"
                      data-testid="warehouse-negative-badge"
                      title="Kho đang cho phép xuất khi tồn chưa đủ"
                    >
                      Cho xuất âm
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right apv-col-actions">
                  <div className="flex justify-end gap-1 relative z-10" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-blue-600 hover:text-blue-800"
                      title="Sửa kho"
                      onClick={() => navigate({ to: `/warehouse/master-data/${item.id}/edit` } as any)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-rose-600 hover:text-rose-800"
                      title="Xóa kho"
                      onClick={() => handleDelete(item.id, item.name)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </DataTableRow>
            ))
          )}
        </TableBody>
      </Table>
    </ListPageShell>
  );
}
